import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle, Clock, Flag, RefreshCw, Search, ShieldAlert, ShieldCheck, X } from 'lucide-react'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './FraudManagementScreen.module.css'

const REFRESH_INTERVAL_MS = 180_000

const TABS = [
    { key: 'active', label: 'Active Flags' },
    { key: 'cleared', label: 'Cleared' },
]

function formatDateTime(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(String(dateStr).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    })
}

function fmtAmount(amount) {
    if (amount == null) return '—'
    return `₹${Number(amount).toLocaleString('en-IN')}`
}

// ─── CLEAR FLAG MODAL ─────────────────────────────────────────────────────────
function ClearFlagModal({ referral, onClose, onDone }) {
    const authCtx = useContext(AuthContext)
    const [clearanceNotes, setClearanceNotes] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const handleSubmit = async () => {
        setError('')
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralClearFraudFlag', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    referralId: referral.referralId,
                    clearanceNotes: clearanceNotes.trim() || undefined,
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                onDone('Flag cleared. Reward processing restored.', false)
            } else {
                const errType = data.response?.errorType
                if (errType === 'NOT_FLAGGED') {
                    onDone('Referral is not currently flagged. Refresh and try again.', true)
                } else if (errType === 'NOT_FOUND') {
                    setError('Referral not found.')
                } else {
                    if (data.message === 'token Expired, please login again!') authCtx.logout()
                    else setError(data.message || 'Could not clear flag. Try again.')
                }
            }
        } catch {
            setError('Network error. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Clear Fraud Flag</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isSubmitting}><X size={13} /></button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.modalInfoChip}>
                        <ShieldCheck size={13} color="#15803d" />
                        <span className={styles.modalInfoId}>{referral.referralId}</span>
                    </div>
                    <div className={styles.infoBanner}>
                        <ShieldCheck size={12} />
                        <span>This will restore normal reward processing for this referral.</span>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Clearance Notes (optional)</label>
                        <textarea
                            className={styles.formTextarea}
                            value={clearanceNotes}
                            onChange={e => setClearanceNotes(e.target.value)}
                            placeholder="e.g. Investigation complete — false positive. Device shared by family members."
                            disabled={isSubmitting}
                            rows={3}
                        />
                    </div>
                    {error && <div className={styles.errorText}><AlertTriangle size={11} />{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button
                        className={styles.btnSuccess}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? <><RefreshCw size={12} className={styles.spinIcon} /> Clearing...</>
                            : <><ShieldCheck size={12} /> Clear Flag</>}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isSubmitting}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

// ─── REFERRAL ROW ─────────────────────────────────────────────────────────────
function ReferralRow({ r, isActive, onClear }) {
    return (
        <div className={`${styles.queueRow} ${isActive ? styles.rowFlagged : styles.rowCleared}`}>
            <div className={styles.rowMain}>
                <div className={styles.rowLeft}>
                    <div className={styles.referralId}>{r.referralId}</div>
                    {r.referreeName && <div className={styles.stylistName}>{r.referreeName}</div>}
                    {r.referreePhone && <div className={styles.stylistMeta}>{r.referreePhone}</div>}
                    <div className={styles.metaLine}>
                        <Clock size={10} />
                        <span>{isActive ? 'Flagged' : 'Cleared'}: {formatDateTime(isActive ? r.flaggedOn : r.clearedOn)}</span>
                        {(isActive ? r.flaggedBy : r.clearedBy) && (
                            <>
                                <span className={styles.dot}>·</span>
                                <span>by {isActive ? r.flaggedBy : r.clearedBy}</span>
                            </>
                        )}
                    </div>
                    {isActive && r.fraudFlagReason && (
                        <div className={styles.reasonDisplay}>{r.fraudFlagReason}</div>
                    )}
                    {!isActive && r.clearanceNotes && (
                        <div className={styles.clearanceDisplay}>{r.clearanceNotes}</div>
                    )}
                </div>

                <div className={styles.rowRight}>
                    {isActive && r.rewardsHeld != null && (
                        <div className={styles.rewardsHeldChip}>
                            <ShieldAlert size={10} />
                            <span>{fmtAmount(r.rewardsHeld)} held</span>
                        </div>
                    )}
                    <div className={styles.badgeRow}>
                        {isActive
                            ? <span className={`${styles.badge} ${styles.badgeFlagged}`}><Flag size={9} /> Fraud Flagged</span>
                            : <span className={`${styles.badge} ${styles.badgeCleared}`}><ShieldCheck size={9} /> Cleared</span>
                        }
                    </div>
                    {isActive && (
                        <div className={styles.actionRow}>
                            <button className={styles.btnClearFlag} onClick={onClear}>
                                <ShieldCheck size={11} /> Clear Flag
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const FraudManagementScreen = () => {
    const authCtx = useContext(AuthContext)
    const [referrals, setReferrals] = useState([])
    const [summary, setSummary] = useState(null)
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, hasMore: false })
    const [page, setPage] = useState(1)
    const [activeTab, setActiveTab] = useState('active')
    const [searchQuery, setSearchQuery] = useState('')
    const [isFetching, setIsFetching] = useState(false)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [secondsAgo, setSecondsAgo] = useState(0)
    const [clearModal, setClearModal] = useState(null)
    const [toast, setToast] = useState(null)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3500)
    }

    const fetchQueue = useCallback(async (tab, p, search) => {
        setIsFetching(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralGetFraudQueue', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    type: tab,
                    page: p,
                    limit: 20,
                    ...(search ? { search } : {}),
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                setReferrals(data.response?.queue || [])
                setSummary(data.response?.summary || null)
                setPagination(data.response?.pagination || { page: 1, limit: 20, total: 0, hasMore: false })
                setLastUpdated(Date.now())
                setSecondsAgo(0)
            } else {
                if (data.message === 'token Expired, please login again!') authCtx.logout()
            }
        } catch (err) {
            console.error('fetchQueue error:', err)
        } finally {
            setIsFetching(false)
        }
    }, [authCtx.token, authCtx.userIdentifier])

    useEffect(() => {
        setPage(1)
        fetchQueue(activeTab, 1, searchQuery)
    }, [activeTab, fetchQueue])

    useEffect(() => {
        const id = setInterval(() => fetchQueue(activeTab, page, searchQuery), REFRESH_INTERVAL_MS)
        return () => clearInterval(id)
    }, [fetchQueue, activeTab, page, searchQuery])

    useEffect(() => {
        const id = setInterval(() => {
            if (lastUpdated) setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000))
        }, 1000)
        return () => clearInterval(id)
    }, [lastUpdated])

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        setPage(1)
        fetchQueue(activeTab, 1, searchQuery)
    }

    const handleTabChange = (tab) => {
        if (activeTab === tab) return
        setSearchQuery('')
        setActiveTab(tab)
    }

    const handlePageChange = (newPage) => {
        setPage(newPage)
        fetchQueue(activeTab, newPage, searchQuery)
    }

    const handleActionDone = (msg, isError) => {
        setClearModal(null)
        if (msg) showToast(msg, isError ? 'error' : 'success')
        fetchQueue(activeTab, page, searchQuery)
    }

    const isInitialLoad = isFetching && referrals.length === 0
    const isActive = activeTab === 'active'

    return (
        <div className={styles.mainWrapper}>
            {clearModal && (
                <ClearFlagModal
                    referral={clearModal}
                    onClose={() => setClearModal(null)}
                    onDone={handleActionDone}
                />
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
                    {toast.type === 'error' ? <AlertTriangle size={13} /> : <CheckCircle size={13} />}
                    {toast.message}
                </div>
            )}

            {/* ── HEADER ──────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <ShieldAlert size={15} color="#dc2626" />
                    <span className={styles.pageTitle}>Fraud Management</span>
                    <div className={styles.refreshIndicator}>
                        {isFetching
                            ? <RefreshCw size={11} className={styles.spinIcon} />
                            : lastUpdated && <span className={styles.lastUpdatedText}>Updated {secondsAgo}s ago</span>
                        }
                    </div>
                </div>
                {summary && (
                    <div className={styles.summaryBar}>
                        <div className={styles.summaryChip}>
                            <span className={styles.summaryNum}>{summary.activeFlagCount}</span>
                            <span className={styles.summaryLabel}>Active</span>
                        </div>
                        <div className={styles.summaryChip}>
                            <span className={styles.summaryNum}>{fmtAmount(summary.totalAmountAtRisk)}</span>
                            <span className={styles.summaryLabel}>At Risk</span>
                        </div>
                        <div className={styles.summaryChip}>
                            <span className={styles.summaryNum}>{summary.flaggedThisWeek}</span>
                            <span className={styles.summaryLabel}>This Week</span>
                        </div>
                    </div>
                )}
            </div>

            {/* ── TABS + SEARCH ────────────────────────────────────── */}
            <div className={styles.tabBar}>
                <div className={styles.tabGroup}>
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
                    <Search size={12} className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search referral ID..."
                    />
                </form>
            </div>

            {/* ── QUEUE BODY ───────────────────────────────────────── */}
            <div className={styles.queueBody}>
                {isInitialLoad ? (
                    <div className={styles.loadingState}>
                        <RefreshCw size={22} className={styles.spinIcon} />
                        <span>Loading...</span>
                    </div>
                ) : referrals.length === 0 ? (
                    <div className={styles.emptyState}>
                        <ShieldCheck size={40} color="#16a34a" />
                        <div className={styles.emptyTitle}>
                            {isActive ? 'No active fraud flags.' : 'No cleared flags yet.'}
                        </div>
                    </div>
                ) : (
                    <div className={styles.queueList}>
                        {referrals.map(r => (
                            <ReferralRow
                                key={r.referralId}
                                r={r}
                                isActive={isActive}
                                onClear={() => setClearModal(r)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ── PAGINATION ───────────────────────────────────────── */}
            {pagination.total > 0 && (
                <div className={styles.paginationBar}>
                    <span className={styles.paginationInfo}>
                        Showing {referrals.length} of {pagination.total}
                    </span>
                    <div className={styles.pageControls}>
                        <button
                            className={styles.pageBtn}
                            disabled={page <= 1}
                            onClick={() => handlePageChange(page - 1)}
                        >
                            ← Prev
                        </button>
                        <span className={styles.pageNum}>
                            Page {pagination.page} of {pagination.totalPages || Math.ceil(pagination.total / pagination.limit) || 1}
                        </span>
                        <button
                            className={styles.pageBtn}
                            disabled={!pagination.hasMore}
                            onClick={() => handlePageChange(page + 1)}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default FraudManagementScreen
